/**
 * Life4Earth V2 — System Settings API Adapter
 *
 * Two-tier design:
 *   1. Specialised endpoints (passcode) — existing, purpose-built routes.
 *   2. Generic key/value settings — simple GET/PUT for arbitrary config
 *      like video URLs, feature text, limits, etc.
 *
 * Backend contract (generic):
 *   GET  /api/SystemSettings/{key}          → { key, value }
 *   PUT  /api/SystemSettings/{key}          → { key, value }  (body: { value })
 *   GET  /api/SystemSettings?prefix={pfx}   → [{ key, value }, …]
 *
 * All values are strings. The frontend is responsible for parsing if needed.
 */

import { apiFetch } from './client';

// ── Well-Known Setting Keys ─────────────────────────────────────────

/** Canonical keys for system settings used in the UI. */
export const SETTING_KEYS = {
  /** YouTube/video embed URL shown on the project creation stepper. */
  STEPPER_VIDEO_URL: 'platform.stepper_video_url',

  /** NewsAPI.ai integration settings. */
  NEWS_API_KEY: 'news.api_key',
  NEWS_API_ENABLED: 'news.api_enabled',
  NEWS_API_PROVIDER: 'news.provider',             // 'newsapi_ai' (default)
  NEWS_API_BASE_URL: 'news.api_base_url',         // default: https://eventregistry.org/api/v1
  NEWS_DEFAULT_LANGUAGE: 'news.default_language',  // default: 'eng'
  NEWS_MAX_ARTICLES_PER_FETCH: 'news.max_articles_per_fetch', // default: 15

  /** Registration mode (DEV-145): "true" = open registration, "false" = invitation-only. */
  ALLOW_PUBLIC_REGISTRATION: 'allow_public_registration',

  /** Sign-in method toggles (Admin Console → User Management). "true"/"false". */
  AUTH_GOOGLE_ENABLED: 'auth.google_enabled',
  AUTH_EMAIL_PASSWORD_ENABLED: 'auth.email_password_enabled',
  AUTH_APPLE_ENABLED: 'auth.apple_enabled',

  /** Block the logged-in app on phone-sized screens (true/false). Landing/login unaffected. */
  BLOCK_SMALL_DEVICES: 'ui.block_small_devices',

  /** Comma-separated list of enabled language codes (e.g. "en,fr,de"). English always included. */
  ENABLED_LANGUAGES: 'platform.enabled_languages',
} as const;

// ── Generic Key/Value Settings ──────────────────────────────────────

export interface SystemSetting {
  key: string;
  value: string;
}

/**
 * Fetch a single setting by key.
 * Returns `null` if the key does not exist (404).
 */
export async function getSystemSetting(key: string): Promise<string | null> {
  try {
    const result = await apiFetch<SystemSetting>(`/api/SystemSettings/${encodeURIComponent(key)}`);
    return result.value ?? null;
  } catch (err: any) {
    if (err?.status === 404 || err?.message?.includes('404')) return null;
    throw err;
  }
}

/**
 * Fetch all settings matching a key prefix.
 * E.g. prefix "platform." returns all platform-level settings.
 */
export async function getSystemSettings(prefix?: string): Promise<SystemSetting[]> {
  const qs = prefix ? `?prefix=${encodeURIComponent(prefix)}` : '';
  return apiFetch<SystemSetting[]>(`/api/SystemSettings${qs}`);
}

/**
 * Create or update a single setting.
 */
export async function putSystemSetting(key: string, value: string): Promise<SystemSetting> {
  return apiFetch<SystemSetting>(`/api/SystemSettings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  });
}

// ── Registration Mode (DEV-145) ─────────────────────────────────────

/**
 * Fetch the current registration mode (public endpoint, no auth required).
 * GET /api/SystemSettings/registration-mode
 * Returns whether open (public) registration is enabled.
 */
export async function getRegistrationMode(): Promise<{ allowPublicRegistration: boolean }> {
  // Use raw fetch (not apiFetch) since this endpoint is public and
  // the user may not be authenticated yet.
  const url = `${(await import('./client')).API_BASE_URL}/api/SystemSettings/registration-mode`;
  const res = await fetch(url);
  if (!res.ok) return { allowPublicRegistration: false };
  return res.json();
}

// ── Passcode Verification ────────────────────────────────────────────

/**
 * Verify a passcode guess against the stored hash.
 * POST /api/systemsettings/passcode/verify
 */
export async function verifyGlobePasscode(
  guess: string
): Promise<{ valid: boolean }> {
  return apiFetch<{ valid: boolean }>('/api/SystemSettings/passcode/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ guess }),
  });
}

// ── Passcode Update (Admin) ──────────────────────────────────────────

/**
 * Set a new globe passcode. Backend hashes it with bcrypt.
 * PUT /api/systemsettings/passcode
 */
export async function updateGlobePasscode(
  newPasscode: string
): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/api/SystemSettings/passcode', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newPasscode }),
  });
}
